import { Reducer } from 'redux'
import { FeedbackTab } from '@chegg-wt/core-wa/constants/paraphraser'
import { Feedback, FeedbackType, PaperStatus } from '@chegg-wt/core-wa/types'
import { ActionTypes } from './constants'
import { ParaphraserState, SentenceOffset } from './types'

export const initialState: ParaphraserState = {
  activeFeedbackTab: FeedbackTab.NO_ACTIVE_TAB,
  feedbackList: [],
  fetchNewSentencesLoading: false,
  isPaperCheckInProgress: false,
  rewritePanelSentences: [],
  quickActionPanelType: null,
  activeFeedbackCardId: null,
  isTimedOut: false,
  paperId: null,
  paperTitle: '',
  paperStatus: PaperStatus.DRAFT,
  paraphraseFlag: null,
  sentenceOffsets: [],
  requiresReCheck: true,
  totalFeedbackCount: 0,
  wordCount: 0,
  toastType: null,
  isNewDocumentRequested: false,
  isTransferOfOwnershipInProgress: false,
  undoEnabled: false,
  redoEnabled: false,
  paperList: [],
  isLoggingOut: false,
}

//! Not able to import this function from outside this file
const feedbackPosition = (feedback: Feedback, sentences: SentenceOffset[]): number => {
  const sentence = sentences.find((s) => s.id === feedback.sentenceId)
  const sentenceOffset = sentence?.offset || 0
  return (
    sentenceOffset +
    (feedback.feedbackType === FeedbackType.GRAMMAR ? feedback.position.offset : 0)
  )
}

const insertInSortedFeedback = (
  feedbackList: Feedback[],
  feedbacks: Feedback[],
  sentences: SentenceOffset[]
): Feedback[] => {
  feedbacks.forEach((feedback) => {
    const currentOffset = feedbackPosition(feedback, sentences)
    let i = feedbackList.length - 1
    // In most cases it will be added in end itself
    for (i = feedbackList.length - 1; i >= 0; i -= 1) {
      if (currentOffset >= feedbackPosition(feedbackList[i], sentences)) break
    }
    // Add at index i+1;
    feedbackList.splice(i + 1, 0, feedback)
  })
  return [...feedbackList]
}

export const addReasonInFeedback = (
  feedbackList: Feedback[],
  reason: string,
  feedbackId: string
): Feedback[] => {
  const feedbackIndex = feedbackList.findIndex((feedback) => feedback.id === feedbackId)
  if (feedbackIndex === -1) {
    console.error(`feedback id not found, ${feedbackId}`)
    return feedbackList
  }

  const newFeedbackObject: Feedback = { ...feedbackList[feedbackIndex], reason }
  // eslint-disable-next-line no-param-reassign
  feedbackList[feedbackIndex] = newFeedbackObject

  return [...feedbackList]
}

export const paraphraserReducer: Reducer<ParaphraserState> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case ActionTypes.SET_PARAPHRASE_FLAG:
      return { ...state, paraphraseFlag: action.payload.paraphraseFlag }
    case ActionTypes.SET_SENTENCE_OFFSETS:
    case ActionTypes.ADJUST_SENTENCE_OFFSETS:
      return { ...state, sentenceOffsets: action.payload.sentenceOffsets }
    case ActionTypes.SET_FEEDBACKS:
      return { ...state, feedbackList: action.payload.feedbacks }
    case ActionTypes.APPEND_TO_FEEDBACKS: {
      return {
        ...state,
        feedbackList: insertInSortedFeedback(
          state.feedbackList,
          action.payload.feedbacks,
          state.sentenceOffsets
        ),
      }
    }
    case ActionTypes.SET_IS_PAPER_CHECK_IN_PROGRESS:
      return { ...state, isPaperCheckInProgress: action.payload.isPaperCheckInProgress }
    case ActionTypes.SET_ACTIVE_FEEDBACK_TAB:
      return { ...state, activeFeedbackTab: action.payload.activeFeedbackTab }
    case ActionTypes.SET_ACTIVE_FEEDBACK_CARD_ID:
      return { ...state, activeFeedbackCardId: action.payload.activeFeedbackCardId }
    case ActionTypes.SET_IS_TIMED_OUT:
      return { ...state, isTimedOut: action.payload }
    case ActionTypes.UPDATE_TOTAL_FEEDBACK_COUNT:
      return { ...state, totalFeedbackCount: state.feedbackList.length }
    case ActionTypes.SET_REWRITE_PANEL_SENTENCES:
      return { ...state, rewritePanelSentences: action.payload.rewritePanelSentences }
    case ActionTypes.SET_QUICK_ACTION_PANEL_TYPE:
      return { ...state, quickActionPanelType: action.payload.quickActionPanelType }
    case ActionTypes.SET_TOTAL_FEEDBACK_COUNT:
      return { ...state, totalFeedbackCount: action.payload.totalFeedbackCount }
    case ActionTypes.SET_PAPER_ID:
      return { ...state, paperId: action.payload.paperId }
    case ActionTypes.SET_PAPER_TITLE:
      return { ...state, paperTitle: action.payload.paperTitle }
    case ActionTypes.SET_PAPER_STATUS:
      return { ...state, paperStatus: action.payload.paperStatus }
    case ActionTypes.SET_FEEDBACK_REASON: {
      const { reason, feedbackId } = action.payload
      return {
        ...state,
        feedbackList: addReasonInFeedback(state.feedbackList, reason, feedbackId),
      }
    }
    case ActionTypes.SET_PAPER_REQUIRES_RECHECK:
      return { ...state, requiresReCheck: action.payload.requiresReCheck }
    case ActionTypes.SET_IS_TRANSFER_OF_OWNERSHIP_IN_PROGRESS:
      return {
        ...state,
        isTransferOfOwnershipInProgress: action.payload.isTransferOfOwnershipInProgress,
      }
    case ActionTypes.SET_WORD_COUNT:
      return { ...state, wordCount: action.payload.wordCount }
    case ActionTypes.NOTIFY_TOAST:
      return { ...state, toastType: action.payload.type }
    case ActionTypes.CLEAR_TOAST:
      return { ...state, toastType: null }
    case ActionTypes.SET_FETCH_NEW_SENTENCES_LOADING:
      return {
        ...state,
        fetchNewSentencesLoading: action.payload.fetchNewSentencesLoading,
      }

    case ActionTypes.RESET_PAPER: {
      const {
        paperList,
        isNewDocumentRequested,
        ...initialPaperState
      } = initialState
      return {
        ...state,
        ...initialPaperState,
      }
    }
    case ActionTypes.NEW_DOC_CREATION_REQUEST: {
      return {
        ...state,
        isNewDocumentRequested: action.payload.isNewDocumentRequested,
      }
    }

    case ActionTypes.SET_UNDO_ENABLED: {
      return {
        ...state,
        undoEnabled: action.payload,
      }
    }
    case ActionTypes.SET_PAPER_LIST: {
      return {
        ...state,
        paperList: action.payload.paperList,
      }
    }
    case ActionTypes.SET_IS_PAPER_REVIEWED: {
      return {
        ...state,
        isPaperCheckInProgress: action.payload,
      }
    }
    case ActionTypes.SET_REDO_ENABLED: {
      return {
        ...state,
        redoEnabled: action.payload,
      }
    }
    case ActionTypes.IS_LOGGING_OUT: {
      return {
        ...state,
        isLoggingOut: action.payload.isLoggingOut,
      }
    }
    default:
      return state
  }
}

export default paraphraserReducer
